// C:\IntegraNube_WebStore\src\pages\PaginaDinamica.js
'use strict';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Link as RouterLink,
  useNavigate,
  useParams,
} from 'react-router-dom';

import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Container,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import {
  HomeOutlined as HomeIcon,
  NavigateNextOutlined as NavigateNextIcon,
  RefreshOutlined as RefreshIcon,
} from '@mui/icons-material';

import api from '../api/api';
import NoEncontrado from './NoEncontrado';

/* ===================================================================== */
/* Helpers                                                               */
/* ===================================================================== */

function cleanString(value) {
  const result = String(value ?? '').trim();
  return result || '';
}

function resolveTiendaData(source) {
  if (!source || typeof source !== 'object') {
    return {};
  }

  if (
    source.status &&
    source.data &&
    typeof source.data === 'object'
  ) {
    return source.data;
  }

  if (
    source.data &&
    typeof source.data === 'object' &&
    (source.data.branding || source.data.nombre)
  ) {
    return source.data;
  }

  return source;
}

function extractProduct(payload) {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  if (payload?.data?.producto) {
    return payload.data.producto;
  }

  if (payload?.producto) {
    return payload.producto;
  }

  if (
    payload?.data &&
    typeof payload.data === 'object' &&
    !Array.isArray(payload.data)
  ) {
    return payload.data;
  }

  return null;
}

function isSafeHref(value) {
  const href = cleanString(value);

  if (!href) return false;

  if (
    href.startsWith('/') ||
    href.startsWith('#') ||
    href.startsWith('./') ||
    href.startsWith('../')
  ) {
    return true;
  }

  try {
    const parsed = new URL(href, window.location.origin);
    return [
      'http:',
      'https:',
      'mailto:',
      'tel:',
    ].includes(parsed.protocol);
  } catch (_) {
    return false;
  }
}

/*
 * Sanitización sin dependencias externas.
 * Conserva formato editorial útil y elimina estilos de Word, scripts,
 * iframes, eventos inline y atributos no autorizados.
 */
function sanitizeHtml(html) {
  const raw = String(html ?? '');

  if (!raw || typeof window === 'undefined') {
    return '';
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(raw, 'text/html');

  const allowedTags = new Set([
    'P',
    'BR',
    'STRONG',
    'B',
    'EM',
    'I',
    'U',
    'S',
    'DEL',
    'H1',
    'H2',
    'H3',
    'H4',
    'H5',
    'H6',
    'UL',
    'OL',
    'LI',
    'A',
    'BLOCKQUOTE',
    'HR',
    'TABLE',
    'THEAD',
    'TBODY',
    'TFOOT',
    'TR',
    'TH',
    'TD',
    'CODE',
    'PRE',
    'SUP',
    'SUB',
  ]);

  const dangerousTags = new Set([
    'SCRIPT',
    'STYLE',
    'IFRAME',
    'OBJECT',
    'EMBED',
    'SVG',
    'MATH',
    'FORM',
    'INPUT',
    'BUTTON',
    'TEXTAREA',
    'SELECT',
    'OPTION',
    'LINK',
    'META',
  ]);

  const sanitizeElement = (element) => {
    Array.from(element.children).forEach((child) => {
      const tag = child.tagName.toUpperCase();

      if (dangerousTags.has(tag)) {
        child.remove();
        return;
      }

      if (!allowedTags.has(tag)) {
        /*
         * Primero saneamos los descendientes y recién después quitamos
         * la etiqueta contenedora. Así un <div><script>...</script></div>
         * no puede hacer que el <script> sobreviva al unwrap.
         */
        sanitizeElement(child);

        const parent = child.parentNode;

        if (!parent) {
          child.remove();
          return;
        }

        while (child.firstChild) {
          parent.insertBefore(child.firstChild, child);
        }

        child.remove();
        return;
      }

      const originalAttributes =
        Array.from(child.attributes);

      originalAttributes.forEach((attribute) => {
        child.removeAttribute(attribute.name);
      });

      if (tag === 'A') {
        const original = Object.fromEntries(
          originalAttributes.map((attribute) => [
            attribute.name.toLowerCase(),
            attribute.value,
          ])
        );

        if (isSafeHref(original.href)) {
          child.setAttribute('href', original.href);
        }

        if (cleanString(original.title)) {
          child.setAttribute(
            'title',
            cleanString(original.title)
          );
        }

        if (original.target === '_blank') {
          child.setAttribute('target', '_blank');
          child.setAttribute(
            'rel',
            'noopener noreferrer'
          );
        }
      }

      if (tag === 'TH' || tag === 'TD') {
        const original = Object.fromEntries(
          originalAttributes.map((attribute) => [
            attribute.name.toLowerCase(),
            attribute.value,
          ])
        );

        ['colspan', 'rowspan'].forEach((name) => {
          const number = Number.parseInt(
            original[name],
            10
          );

          if (
            Number.isInteger(number) &&
            number >= 1 &&
            number <= 20
          ) {
            child.setAttribute(name, String(number));
          }
        });
      }

      sanitizeElement(child);
    });
  };

  sanitizeElement(doc.body);

  return doc.body.innerHTML;
}

function setOrCreateMeta(name, content) {
  if (typeof document === 'undefined') return null;

  let element = document.querySelector(
    `meta[name="${name}"]`
  );

  const existed = Boolean(element);
  const previous = element?.getAttribute('content') ?? null;

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('name', name);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content || '');

  return {
    element,
    existed,
    previous,
  };
}

function setCanonical(url) {
  if (typeof document === 'undefined') return null;

  let element = document.querySelector(
    'link[rel="canonical"]'
  );

  const existed = Boolean(element);
  const previous = element?.getAttribute('href') ?? null;

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }

  element.setAttribute('href', url);

  return {
    element,
    existed,
    previous,
  };
}

function restoreManagedElement(state, attribute) {
  if (!state?.element) return;

  if (!state.existed) {
    state.element.remove();
    return;
  }

  if (state.previous === null) {
    state.element.removeAttribute(attribute);
  } else {
    state.element.setAttribute(
      attribute,
      state.previous
    );
  }
}

/* ===================================================================== */
/* Página dinámica                                                       */
/* ===================================================================== */

export default function PaginaDinamica(props) {
  const { slug: rawSlug } = useParams();
  const navigate = useNavigate();

  const tienda = resolveTiendaData(
    props?.tienda ||
      props?.store ||
      props?.storeConfig ||
      {}
  );

  const slug = cleanString(rawSlug).toLowerCase();

  const [pagina, setPagina] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);

  const contenidoSeguro = useMemo(
    () => sanitizeHtml(pagina?.contenidoHtml),
    [pagina?.contenidoHtml]
  );

  const resolveLegacyProduct = useCallback(
    async (signal) => {
      try {
        const response = await api.get(
          `/productos/${encodeURIComponent(slug)}`,
          {
            signal,
            timeout: 6000,
            retries: 1,
          }
        );

        const product = extractProduct(
          response?.data || {}
        );

        if (!product) {
          return false;
        }

        const productSlug = cleanString(
          product.slug
        );

        const identifier =
          productSlug ||
          cleanString(product.id) ||
          slug;

        navigate(
          `/producto/${encodeURIComponent(identifier)}`,
          { replace: true }
        );

        return true;
      } catch (legacyError) {
        if (
          legacyError?.name === 'CanceledError' ||
          legacyError?.code === 'ERR_CANCELED'
        ) {
          throw legacyError;
        }

        if (legacyError?.response?.status === 404) {
          return false;
        }

        /*
         * Si falla la comprobación legacy por una caída transitoria,
         * no la convertimos silenciosamente en 404.
         */
        throw legacyError;
      }
    },
    [navigate, slug]
  );

  const loadPage = useCallback(
    async (signal) => {
      setLoading(true);
      setError('');
      setNotFound(false);
      setPagina(null);

      if (!slug) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(
          `/paginas/${encodeURIComponent(slug)}`,
          {
            signal,
            timeout: 7000,
            retries: 2,
          }
        );

        const payload = response?.data || {};

        if (
          payload?.status !== 'ok' ||
          !payload?.data
        ) {
          throw new Error(
            payload?.message ||
              'La página devolvió una respuesta inválida.'
          );
        }

        setPagina(payload.data);
      } catch (requestError) {
        if (
          requestError?.name === 'CanceledError' ||
          requestError?.code === 'ERR_CANCELED'
        ) {
          return;
        }

        if (requestError?.response?.status === 404) {
          try {
            const redirected =
              await resolveLegacyProduct(signal);

            if (!redirected) {
              setNotFound(true);
            }
          } catch (legacyError) {
            if (
              legacyError?.name === 'CanceledError' ||
              legacyError?.code === 'ERR_CANCELED'
            ) {
              return;
            }

            console.error(
              '[PaginaDinamica] Error resolviendo producto legacy:',
              legacyError
            );

            setError(
              legacyError?.friendlyMessage ||
                legacyError?.response?.data?.message ||
                'No fue posible verificar esta dirección. Intente nuevamente.'
            );
          }

          return;
        }

        console.error(
          '[PaginaDinamica] Error cargando página:',
          requestError
        );

        setError(
          requestError?.friendlyMessage ||
            requestError?.response?.data?.message ||
            requestError?.message ||
            'No fue posible cargar esta página.'
        );
      } finally {
        setLoading(false);
      }
    },
    [resolveLegacyProduct, slug]
  );

  useEffect(() => {
    const controller = new AbortController();

    loadPage(controller.signal);

    return () => {
      controller.abort();
    };
  }, [loadPage]);

  /* =================================================================== */
  /* SEO en navegación SPA                                               */
  /* =================================================================== */

  useEffect(() => {
    if (!pagina || typeof document === 'undefined') {
      return undefined;
    }

    const previousTitle = document.title;
    const storeName = cleanString(tienda?.nombre);

    const title =
      cleanString(pagina?.seo?.title) ||
      (storeName
        ? `${pagina.titulo} | ${storeName}`
        : pagina.titulo);

    document.title = title;

    const descriptionState = setOrCreateMeta(
      'description',
      cleanString(pagina?.seo?.description)
    );

    const robotsState = setOrCreateMeta(
      'robots',
      pagina?.seo?.indexable === false
        ? 'noindex,follow'
        : 'index,follow'
    );

    const canonicalDomain = cleanString(
      props?.storeMeta?.primaryDomain ||
      props?.storeMeta?.domain
    );

    const canonicalBase =
      canonicalDomain
        ? `https://${canonicalDomain}`
        : window.location.origin;

    const canonicalState = setCanonical(
      `${canonicalBase}/${encodeURIComponent(
        pagina.slug
      )}`
    );

    return () => {
      document.title = previousTitle;

      restoreManagedElement(
        descriptionState,
        'content'
      );

      restoreManagedElement(
        robotsState,
        'content'
      );

      restoreManagedElement(
        canonicalState,
        'href'
      );
    };
  }, [pagina, tienda?.nombre]);

  if (notFound) {
    return <NoEncontrado {...props} />;
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
        <Box
          sx={{
            minHeight: 280,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Stack spacing={2} alignItems="center">
            <CircularProgress size={36} />
            <Typography color="text.secondary">
              Cargando página…
            </Typography>
          </Stack>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 7 } }}>
        <Stack spacing={2}>
          <Alert severity="warning">
            {error}
          </Alert>

          <Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => loadPage()}
            >
              Reintentar
            </Button>
          </Box>
        </Stack>
      </Container>
    );
  }

  if (!pagina) {
    return <NoEncontrado {...props} />;
  }

  return (
    <Container
      component="article"
      maxWidth="lg"
      sx={{ py: { xs: 4, md: 6 } }}
    >
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="Navegación"
        sx={{ mb: 3 }}
      >
        <Link
          component={RouterLink}
          to="/"
          underline="hover"
          color="inherit"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.6,
          }}
        >
          <HomeIcon fontSize="small" />
          Inicio
        </Link>

        <Typography color="text.primary">
          {pagina.titulo}
        </Typography>
      </Breadcrumbs>

      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2.5, sm: 4, md: 5 },
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <Typography
          component="h1"
          variant="h3"
          sx={{
            fontWeight: 800,
            fontSize: {
              xs: '2rem',
              md: '2.55rem',
            },
            letterSpacing: '-0.025em',
            mb: 3.5,
          }}
        >
          {pagina.titulo}
        </Typography>

        {contenidoSeguro ? (
          <Box
            className="cms-content"
            dangerouslySetInnerHTML={{
              __html: contenidoSeguro,
            }}
            sx={{
              color: 'text.primary',
              fontSize: '1rem',
              lineHeight: 1.78,

              '& p': {
                my: 1.8,
              },

              '& h1, & h2, & h3, & h4, & h5, & h6': {
                color: 'text.primary',
                lineHeight: 1.25,
                mt: 4,
                mb: 1.5,
                fontWeight: 800,
              },

              '& h1': { fontSize: '2rem' },
              '& h2': { fontSize: '1.65rem' },
              '& h3': { fontSize: '1.35rem' },
              '& h4': { fontSize: '1.15rem' },

              '& ul, & ol': {
                pl: 3.2,
                my: 2,
              },

              '& li': {
                mb: 0.7,
              },

              '& a': {
                color: 'primary.main',
                textDecorationThickness: '1px',
                textUnderlineOffset: '3px',
              },

              '& blockquote': {
                m: 0,
                my: 2.5,
                pl: 2.5,
                py: 0.5,
                borderLeft: 4,
                borderColor: 'primary.main',
                color: 'text.secondary',
              },

              '& hr': {
                border: 0,
                borderTop: 1,
                borderColor: 'divider',
                my: 4,
              },

              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                my: 3,
              },

              '& th, & td': {
                border: 1,
                borderColor: 'divider',
                px: 1.5,
                py: 1.1,
                textAlign: 'left',
                verticalAlign: 'top',
              },

              '& th': {
                fontWeight: 800,
                bgcolor: 'action.hover',
              },

              '& pre': {
                overflowX: 'auto',
                p: 2,
                borderRadius: 2,
                bgcolor: 'action.hover',
              },

              '& code': {
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
              },

              '& > :first-of-type': {
                mt: 0,
              },

              '& > :last-child': {
                mb: 0,
              },
            }}
          />
        ) : (
          <Typography color="text.secondary">
            Esta página no tiene contenido disponible.
          </Typography>
        )}
      </Paper>
    </Container>
  );
}
